import { CommonModule, Location } from '@angular/common';
import { Component } from '@angular/core';

import { ToastrService } from 'ngx-toastr';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import {
    ColumnsViewDef,
    ContextRoleService,
    GlobalsService,
    MetaService,
    ReadUserApproversRequestDTO,
    UserApproverDTO,
    UserContextRoleDTO,
    UsersService,
    isAssigned,
} from '../../data';
import { UIBlockerComponent } from '@data/components/ui-blocker/ui-blocker.componet';
import { DataTableComponent } from '@data/components/data-table/data-table.component';
import { UserContextRoleScopeProfileBase } from '../user-context-role-scope-profile/user-context-role-scope-profile-base';

@Component({
    standalone: true,
    imports: [CommonModule, TranslateModule, ButtonModule, TooltipModule, UIBlockerComponent, DataTableComponent],
    selector: 'app-account-information-profile-approvers',
    templateUrl: './account-information-profile-approvers.component.html',
    styleUrls: ['./account-information-profile-approvers.component.scss'],
    providers: [ConfirmationService],
    host: {
        class: 'flex flex-column',
        style: 'height: min(50rem, 70vh); max-height: min(50rem, 70vh);',
    },
})
export class AccountInformationProfileApproversComponent extends UserContextRoleScopeProfileBase {
    columns = new ColumnsViewDef([
        {
            name: 'approverEmail',
            header: 'Approver Email',
            width: '33%',
        },
        {
            name: 'approverName',
            header: 'Approver Name',
            width: '33%',
        },
        {
            name: 'approverContextRoleName',
            header: 'Approver Context & Role Name',
            width: '33%',
        },
    ]);

    isLoadingUserApprovers = false;
    userApprovers: UserApproverDTO[] = [];

    constructor(
        private readonly _userSrv: UsersService,
        confirmSrv: ConfirmationService,
        dialogSrv: DialogService,
        contextRoleSrv: ContextRoleService,
        dialogRef: DynamicDialogRef,
        dialogCfg: DynamicDialogConfig,
        translateSrv: TranslateService,
        toastr: ToastrService,
        globals: GlobalsService,
        metaSrv: MetaService,
        location: Location,
    ) {
        super(confirmSrv, dialogSrv, contextRoleSrv, dialogRef, dialogCfg, translateSrv, toastr, globals, metaSrv, location);

        this._translateKey = `${this._translateKey}.approvers`;
    }

    override get isLoadingInternal(): boolean {
        return super.isLoadingInternal || this.isLoadingUserApprovers;
    }

    protected override onInit(): void {
        super.onInit();

        this.translateColumns(this.columns.columns);
    }

    protected override get canReadProfilePermissions(): boolean {
        return false;
    }

    protected override onLangChanged(): void {
        super.onLangChanged();

        this.readUserApprovers();
    }

    readUserApprovers() {
        if (this.requestSubs.length > 1) {
            return;
        }

        this.isLoadingUserApprovers = true;

        this.userApprovers = [];

        let request: ReadUserApproversRequestDTO = {
            contextCd: this.contextRole!.contextCd,
            roleCd: this.contextRole!.roleCd,
            contextProfileCd: !isAssigned(this.userRoleContextProfileId) ? this.profileCd : undefined,
            userEmail: this.contextRole!.information?.userEmail,
            userRoleContextProfileId: this.userRoleContextProfileId,
        };

        this.requestSubs.push(
            this._userSrv.readUserApprovers(request).subscribe({
                next: (response) => {
                    this.isLoadingUserApprovers = false;
                    this.userApprovers = response.userApprovers ?? [];
                },
                error: (error) => {
                    this.isLoadingUserApprovers = false;
                    this.showErrorMessage(error);
                },
            }),
        );
    }
}

export function showProfileUserApprovers(
    dialogSrv: DialogService,
    profileCd: string | undefined,
    userRoleContextProfileId: number | undefined,
    contextRole: UserContextRoleDTO,
) {
    const refApprovers = dialogSrv.open(AccountInformationProfileApproversComponent, {
        showHeader: false,
        width: '90%',
        // height: '50rem',
        contentStyle: { overflow: 'hidden' },
        data: {
            userEmail: contextRole.information?.userEmail,
            contextRole: contextRole,
            profileCd: profileCd?.toLowerCase(),
            userRoleContextProfileId: userRoleContextProfileId,
            doCancel: () => {
                refApprovers.close();
            },
        },
    });
}
